import { useState } from "react";
import { Link, Navigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useIntent } from "@/context/IntentContext";
import { apiPost } from "@/api/client";
import { PublicNav } from "@/components/layout/PublicNav";

export function LoginPage() {
  const { user, login, isLoading } = useAuth();
  const { open: openIntent } = useIntent();
  const [searchParams] = useSearchParams();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [showForgot, setShowForgot] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotSent, setForgotSent] = useState(false);
  const [forgotSubmitting, setForgotSubmitting] = useState(false);
  const [forgotError, setForgotError] = useState("");

  const justRegistered = searchParams.get("registered") === "true";

  if (isLoading) return null;
  if (user) return <Navigate to="/dashboard" replace />;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      await login(username, password);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Login failed");
    } finally {
      setSubmitting(false);
    }
  };

  const inputStyle = {
    background: "#28252a",
    border: "1px solid rgba(255,255,255,0.06)",
    color: "#f0ebe5",
    fontFamily: "'Figtree', sans-serif",
  };

  const inputFocus = "focus:outline-none focus:ring-1 focus:ring-[#c8913a]/40 focus:border-[#c8913a]/30";

  return (
    <div
      className="brand-page min-h-dvh flex flex-col"
      style={{ background: "#141215" }}
    >
      <PublicNav />

      <div className="flex-1 flex items-center justify-center px-4 py-20 relative">
        <div
          className="absolute top-1/4 left-1/2 -translate-x-1/2 w-[500px] h-[500px] pointer-events-none"
          style={{
            background: "radial-gradient(circle, rgba(200,145,58,0.08) 0%, transparent 60%)",
          }}
        />

        <div
          className="relative w-full max-w-sm p-6 sm:p-8"
          style={{
            background: "#1e1b1f",
            border: "1px solid rgba(255,255,255,0.06)",
          }}
        >
          <div className="text-center mb-8">
            <Link
              to="/"
              className="text-2xl font-bold tracking-tight transition-colors"
              style={{
                color: "#c8913a",
                fontFamily: "'Archivo', sans-serif",
              }}
            >
              SellMoreTickets
            </Link>
            <p
              className="text-sm mt-1"
              style={{ color: "#635c57", fontFamily: "'Figtree', sans-serif" }}
            >
              Ticket Intelligence Platform
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {justRegistered && (
              <div
                className="text-sm px-4 py-2.5"
                style={{
                  background: "rgba(40,218,144,0.08)",
                  border: "1px solid rgba(40,218,144,0.2)",
                  color: "#28da90",
                }}
              >
                Account created! Please log in.
              </div>
            )}

            {error && (
              <div
                className="text-sm px-4 py-2.5"
                style={{
                  background: "rgba(203,80,95,0.08)",
                  border: "1px solid rgba(203,80,95,0.2)",
                  color: "#cb505f",
                }}
              >
                {error}
              </div>
            )}

            <div>
              <label
                htmlFor="username"
                className="block text-xs font-medium mb-1.5"
                style={{ color: "#9a918a", fontFamily: "'Figtree', sans-serif" }}
              >
                Email
              </label>
              <input
                id="username"
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                required
                autoFocus
                autoComplete="email"
                className={`w-full px-4 py-2.5 text-sm placeholder:text-[#635c57] transition-colors ${inputFocus}`}
                style={inputStyle}
                placeholder="Enter your email"
              />
            </div>

            <div>
              <label
                htmlFor="password"
                className="block text-xs font-medium mb-1.5"
                style={{ color: "#9a918a", fontFamily: "'Figtree', sans-serif" }}
              >
                Password
              </label>
              <input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                autoComplete="current-password"
                className={`w-full px-4 py-2.5 text-sm placeholder:text-[#635c57] transition-colors ${inputFocus}`}
                style={inputStyle}
                placeholder="Enter password"
              />
            </div>

            <button
              type="submit"
              disabled={submitting}
              className="w-full font-semibold px-4 py-2.5 text-sm transition-all hover:brightness-110 disabled:opacity-50 disabled:cursor-not-allowed"
              style={{
                background: "#c8913a",
                color: "#141215",
                fontFamily: "'Figtree', sans-serif",
                boxShadow: "0 0 24px rgba(200,145,58,0.2)",
              }}
            >
              {submitting ? "Signing in..." : "Sign in"}
            </button>

            <div className="text-right">
              <button
                type="button"
                onClick={() => setShowForgot(!showForgot)}
                className="text-xs transition-colors hover:underline underline-offset-2"
                style={{ color: "#c8913a", fontFamily: "'Figtree', sans-serif" }}
              >
                Forgot password?
              </button>
            </div>
          </form>

          {showForgot && (
            <div
              className="mt-4 p-4"
              style={{
                background: "rgba(200,145,58,0.04)",
                border: "1px solid rgba(200,145,58,0.12)",
              }}
            >
              {forgotSent ? (
                <p className="text-sm text-center" style={{ color: "#28da90" }}>
                  Reset link sent! Check your email.
                </p>
              ) : (
                <>
                  <p
                    className="text-xs mb-3"
                    style={{ color: "#9a918a", fontFamily: "'Figtree', sans-serif" }}
                  >
                    Enter your email and we'll send a password reset link.
                  </p>
                  <div className="flex gap-2">
                    <input
                      type="email"
                      value={forgotEmail}
                      onChange={(e) => setForgotEmail(e.target.value)}
                      placeholder="your@email.com"
                      autoComplete="email"
                      className={`flex-1 px-3 py-2 text-sm placeholder:text-[#635c57] ${inputFocus}`}
                      style={inputStyle}
                    />
                    <button
                      type="button"
                      disabled={!forgotEmail || forgotSubmitting}
                      onClick={async () => {
                        setForgotSubmitting(true);
                        setForgotError("");
                        try {
                          await apiPost("/api/forgot-password", { email: forgotEmail });
                          setForgotSent(true);
                        } catch {
                          setForgotError("Couldn't send the reset email. Please try again.");
                        } finally {
                          setForgotSubmitting(false);
                        }
                      }}
                      className="px-4 py-2 text-sm font-semibold disabled:opacity-50 transition-all hover:brightness-110"
                      style={{
                        background: "#c8913a",
                        color: "#141215",
                        fontFamily: "'Figtree', sans-serif",
                      }}
                    >
                      {forgotSubmitting ? "..." : "Send"}
                    </button>
                  </div>
                  {forgotError && (
                    <p className="text-xs mt-2" style={{ color: "#e57373" }} role="alert">
                      {forgotError}
                    </p>
                  )}
                </>
              )}
            </div>
          )}

          <p
            className="text-center text-sm mt-5"
            style={{ color: "#635c57", fontFamily: "'Figtree', sans-serif" }}
          >
            Don't have an account?{" "}
            <button
              type="button"
              onClick={openIntent}
              className="font-medium underline-offset-2 hover:underline transition-colors"
              style={{ color: "#c8913a" }}
            >
              Sign up free
            </button>
          </p>
        </div>
      </div>
    </div>
  );
}
